import * as Dialog from '@radix-ui/react-dialog';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '../../assets/icons/close.svg?react';
import { cn } from '../../utils/className';

export interface SidePanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** Panel title - main heading */
  title: string;
  /** Panel subtitle - displayed below title */
  subtitle?: string;
  /** Panel content - typically tabs or other content */
  children: ReactNode;
  /** Optional className for the panel content container */
  className?: string;
  /** Shown in the header, before the close button. */
  headerAction?: ReactNode;
  /** Replaces the header's brand colour, for example with a status tone. */
  headerClassName?: string;
  /** Width of the panel - defaults to md (only applies on desktop) */
  width?: 'sm' | 'md' | 'xl';
}

const widthClasses = {
  sm: 'md:w-[320px]',
  md: 'md:w-[560px]',
  xl: 'md:w-[720px]',
};

/**
 * SidePanel - A reusable slide-in panel component
 *
 * Uses Radix Dialog for accessibility and proper focus management.
 *
 * **Desktop (md+)**: Slides in from the right with configurable width
 * **Mobile**: Appears as a full-screen modal from the bottom with fade-in animation
 *
 * @example
 * ```tsx
 * <SidePanel
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Demo Line 1"
 *   subtitle="Cargill · Gent"
 *   width="md"
 * >
 *   <Tabs defaultValue="details">
 *     <TabsList>...</TabsList>
 *     <TabsContent value="details">...</TabsContent>
 *   </Tabs>
 * </SidePanel>
 * ```
 */
export function SidePanel({
  open,
  onOpenChange,
  title,
  subtitle,
  children,
  className,
  headerAction,
  headerClassName,
  width = 'md',
}: SidePanelProps) {
  if (!open) return null;

  return createPortal(
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {/* Backdrop overlay */}
      <Dialog.Overlay
        className='data-[state=open]:animate-in data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed
          inset-0 z-80 bg-black/50'
      />

      {/* Side panel content */}
      <Dialog.Content
        className={cn(
          // Base positioning
          'fixed z-80',
          // Mobile: Full screen
          'inset-0 h-full w-full',
          // Desktop: Right side panel
          'md:top-0 md:right-0 md:bottom-auto md:left-auto md:h-full',
          // Width (desktop only)
          widthClasses[width],
          // Styling
          'flex flex-col bg-white shadow-xl',
          // Animations
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          `md:data-[state=closed]:slide-out-to-right
          md:data-[state=open]:slide-in-from-right`
        )}
        aria-describedby={subtitle ? 'side-panel-subtitle' : undefined}
      >
        {/* Header */}
        <div
          className={cn(
            `bg-brand-primary flex items-start justify-between gap-4 border-b
            border-gray-200 px-4 py-3 md:px-6 md:py-4`,
            headerClassName
          )}
        >
          <div className='min-w-0 flex-1'>
            <Dialog.Title
              className='size-h4 md:size-h4 truncate text-base font-semibold
                text-white'
            >
              {title}
            </Dialog.Title>
            {subtitle && (
              <p
                id='side-panel-subtitle'
                className='size-p2 mt-0.5 truncate text-sm text-white/80
                  md:text-base'
              >
                {subtitle}
              </p>
            )}
          </div>

          {headerAction}

          <button
            className='shrink-0 cursor-pointer rounded-sm p-1 text-white/80
              transition-colors hover:text-white focus:outline-none
              focus-visible:ring-0 focus-visible:outline-none'
            aria-label='Close panel'
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className='h-5 w-5 md:h-5 md:w-5' />
          </button>
        </div>

        {/* Content area with scroll */}
        <div className={cn('flex-1 overflow-y-auto', className)}>
          {children}
        </div>
      </Dialog.Content>
    </Dialog.Root>,
    document.body
  );
}
